/**
 * Canned responses in the dashboard — the logic, kept JSX-free so the node-only Jest setup can
 * test it (work order SUPPORT-TOOLS item 5).
 *
 * Three rules live here rather than in a component, because each is the kind that silently goes
 * wrong in one:
 *
 * - **AC-5.2 — the language follows the ticket, not the operator.** A German operator answering an
 *   English merchant inserts the ENGLISH text. The ticket's `locale` is frozen at creation for the
 *   same reason every support mail follows it; the dashboard's own UI language is irrelevant here.
 * - **Placeholders are filled on insertion**, where the ticket and the signed-in operator are both
 *   known. A value that is not known is left as the literal `{{name}}` and REPORTED, so the operator
 *   sees it before sending rather than the merchant after.
 * - **AC-5.3 — an inserted snippet becomes part of the draft**, not a separate state: the page runs
 *   the result of {@link insertIntoDraft} through `writeDraft`, so a reload after inserting keeps it.
 */

import type { CannedResponseDto } from '../../../../libs/api-client/src/generated/models';

/** The two languages a snippet is written in. */
export type SnippetLanguage = 'de' | 'en';

/**
 * The language whose body a ticket receives.
 *
 * German for a German ticket locale (`de`, `de-AT`, `de_CH` …), English for everything else —
 * English is the only other language a snippet has, and the one every non-German merchant is
 * written to in the rest of the support mail.
 *
 * @param ticketLocale - The ticket's frozen `locale`.
 */
export function snippetLanguage(ticketLocale: string): SnippetLanguage {
  return /^de(?:$|[-_])/i.test(ticketLocale.trim()) ? 'de' : 'en';
}

/**
 * The language the reply box is spell-checked in (its `lang` attribute).
 *
 * A reply goes to the merchant, so it follows the ticket exactly as a snippet does (AC-5.2). An
 * internal note never leaves the team, so it is written — and checked — in the operator's own UI
 * language; checking a German note against an English dictionary would underline every word.
 *
 * @param ticketLocale - The ticket's frozen `locale`.
 * @param isInternal - Whether the draft is an internal note.
 * @param operatorLocale - The dashboard's current UI language.
 */
export function draftLanguage(
  ticketLocale: string,
  isInternal: boolean,
  operatorLocale: SnippetLanguage,
): SnippetLanguage {
  return isInternal ? operatorLocale : snippetLanguage(ticketLocale);
}

/** The body a ticket in this locale receives, unfilled. */
export function snippetBody(
  snippet: Pick<CannedResponseDto, 'bodyDe' | 'bodyEn'>,
  ticketLocale: string,
): string {
  return snippetLanguage(ticketLocale) === 'de'
    ? snippet.bodyDe
    : snippet.bodyEn;
}

/** `{{ name }}` with optional inner whitespace — the same pattern the API validates against. */
const PLACEHOLDER = /\{\{\s*([^{}]*?)\s*\}\}/g;

/** Every placeholder name a text uses, in order of first appearance. */
export function placeholdersIn(text: string): string[] {
  const seen = new Set<string>();
  for (const match of text.matchAll(PLACEHOLDER)) seen.add(match[1]);
  return [...seen];
}

/**
 * The placeholders a text uses that the API does not allow. The API refuses to save these; the
 * editor shows them as the operator types, so the refusal is never the first they hear of it.
 *
 * @param text - A body being edited.
 * @param allowed - The `placeholders` the list endpoint returned.
 */
export function unknownPlaceholders(
  text: string,
  allowed: readonly string[],
): string[] {
  return placeholdersIn(text).filter((name) => !allowed.includes(name));
}

/** What one ticket fills the placeholders with. `null` = not known on this ticket. */
export type PlaceholderValues = Readonly<Record<string, string | null>>;

/**
 * The values a ticket fills in.
 *
 * @param ticket - The ticket's domain and reference.
 * @param agentName - The signed-in operator's display name (the name that signs the reply), or
 *   null when the session predates it.
 */
export function placeholderValues(
  ticket: { readonly shopDomain: string; readonly reference: string },
  agentName: string | null,
): PlaceholderValues {
  return {
    shopName: ticket.shopDomain,
    ticketReference: ticket.reference,
    agentName: agentName?.trim() ? agentName.trim() : null,
  };
}

/** A snippet with its placeholders filled, and the ones that could not be. */
export interface FilledSnippet {
  readonly text: string;
  /** Placeholder names left in the text as `{{name}}` — unknown, or without a value here. */
  readonly unfilled: string[];
}

/**
 * Fills a snippet's placeholders.
 *
 * @param text - The unfilled body.
 * @param values - From {@link placeholderValues}.
 */
export function fillPlaceholders(
  text: string,
  values: PlaceholderValues,
): FilledSnippet {
  const unfilled = new Set<string>();
  const filled = text.replace(PLACEHOLDER, (placeholder, name: string) => {
    const value = values[name];
    if (value === undefined || value === null) {
      unfilled.add(name);
      return placeholder;
    }
    return value;
  });
  return { text: filled, unfilled: [...unfilled] };
}

/**
 * The snippet ready to insert into a ticket's reply: its language chosen by the ticket, its
 * placeholders filled.
 *
 * @param snippet - The snippet.
 * @param ticket - The ticket it goes into.
 * @param agentName - The signed-in operator's display name.
 */
export function prepareSnippet(
  snippet: Pick<CannedResponseDto, 'bodyDe' | 'bodyEn'>,
  ticket: {
    readonly locale: string;
    readonly shopDomain: string;
    readonly reference: string;
  },
  agentName: string | null,
): FilledSnippet & { readonly language: SnippetLanguage } {
  const language = snippetLanguage(ticket.locale);
  return {
    language,
    ...fillPlaceholders(
      snippetBody(snippet, ticket.locale),
      placeholderValues(ticket, agentName),
    ),
  };
}

/**
 * The reply box's contents with a snippet added.
 *
 * APPENDED, separated by a blank line, rather than dropped at the caret: the usual flow is
 * "greeting, then the snippet, then a personal line", and an insert at a caret the agent last left
 * somewhere in a restored draft would put the snippet in the middle of a sentence. An empty box
 * simply receives the snippet.
 *
 * @param current - The composer's text.
 * @param snippet - The filled snippet.
 */
export function insertIntoDraft(current: string, snippet: string): string {
  const head = current.replace(/\s+$/, '');
  const addition = snippet.trim();
  if (!addition) return current;
  return head ? `${head}\n\n${addition}` : addition;
}

/** Where a snippet's text comes from — the marker on the management page. */
export type SnippetOrigin = 'shipped' | 'editedDefault' | 'custom';

/**
 * Whether a snippet is a shipped default still carrying the shipped text, a default an operator
 * has since made their own, or one an operator wrote.
 *
 * It matters to an operator because a SHIPPED text is corrected by the next release on its own —
 * the § 19 wording has gone stale once already — while an edited one never is again.
 */
export function snippetOrigin(
  snippet: Pick<CannedResponseDto, 'key' | 'followsShippedDefault'>,
): SnippetOrigin {
  if (!snippet.key) return 'custom';
  return snippet.followsShippedDefault ? 'shipped' : 'editedDefault';
}

/** The editor's fields. `category` is `''` for "fits any ticket". */
export interface SnippetForm {
  readonly title: string;
  readonly category: string;
  readonly bodyDe: string;
  readonly bodyEn: string;
}

/** The editor pre-filled from a snippet, or empty for a new one. */
export function formFrom(
  snippet?: Pick<
    CannedResponseDto,
    'title' | 'category' | 'bodyDe' | 'bodyEn'
  > | null,
): SnippetForm {
  return {
    title: snippet?.title ?? '',
    category: snippet?.category ?? '',
    bodyDe: snippet?.bodyDe ?? '',
    bodyEn: snippet?.bodyEn ?? '',
  };
}

/** The fields an edit changes, compared with the version it was made against. */
export type SnippetField = keyof SnippetForm;

/**
 * Which fields differ from the base version. An empty list means there is nothing to save — the
 * API would refuse it with 400.
 *
 * @param base - The version the edit is made against.
 * @param form - The editor's contents.
 */
export function changedFields(
  base: SnippetForm,
  form: SnippetForm,
): SnippetField[] {
  const fields: SnippetField[] = ['title', 'category', 'bodyDe', 'bodyEn'];
  return fields.filter((field) =>
    field === 'title'
      ? base.title.trim() !== form.title.trim()
      : base[field] !== form[field],
  );
}

/** Why the editor may not be saved yet, as catalogue keys. Empty = savable. */
export function validateSnippetForm(
  form: SnippetForm,
  allowed: readonly string[],
): string[] {
  const errors: string[] = [];
  if (!form.title.trim()) errors.push('cannedResponses.errors.titleRequired');
  if (!form.bodyDe.trim() || !form.bodyEn.trim()) {
    errors.push('cannedResponses.errors.bothBodiesRequired');
  }
  if (
    unknownPlaceholders(form.bodyDe, allowed).length > 0 ||
    unknownPlaceholders(form.bodyEn, allowed).length > 0
  ) {
    errors.push('cannedResponses.errors.unknownPlaceholder');
  }
  return errors;
}

/**
 * Narrows a string to a member of a generated string enum, or `undefined`.
 *
 * The generator emits one enum per DTO (`CreateCannedResponseDtoCategoryEnum`,
 * `UpdateCannedResponseDtoCategoryEnum`, …) with identical members, and a `<select>` hands back a
 * plain string. A guard, not an assertion: a stale option must be dropped here rather than reach
 * the API as a value it rejects.
 *
 * @param members - `Object.values` of the target enum.
 * @param value - The string to narrow.
 */
export function narrowTo<T extends string>(
  members: readonly T[],
  value: string,
): T | undefined {
  return members.find((member) => member === value);
}

/** The management page's category filter. `''` = all, {@link ANY_CATEGORY} = only the "any ticket" ones. */
export const ANY_CATEGORY = '__any__';

/**
 * The management page's EXACT category filter, applied in the browser.
 *
 * The list endpoint answers a category with "that category plus the ones that fit any ticket",
 * which is what the picker wants and not what someone tidying the catalogue does. The whole list
 * is a few dozen rows, so filtering it here costs nothing and keeps one endpoint.
 *
 * @param snippets - The list as fetched.
 * @param filter - `''`, {@link ANY_CATEGORY}, or a category token.
 */
export function filterByCategory<
  T extends { readonly category?: string | null },
>(snippets: readonly T[], filter: string): T[] {
  if (!filter) return [...snippets];
  if (filter === ANY_CATEGORY) {
    return snippets.filter((snippet) => !snippet.category);
  }
  return snippets.filter((snippet) => snippet.category === filter);
}
